import {
    createAsyncThunk,
    createSelector,
    createSlice,
    PayloadAction,
} from "@reduxjs/toolkit";
import {
    PlannedWorkShiftDto,
    PlannedWorkShiftQueryParameters,
} from "../../../models/plannedWorkShift/plannedWorkShift";
import { Base } from "../../common/base";
import { getPlannedWorkShifts } from "../../../services/plannedWorkShiftService";

interface PlannedWorkShiftState {
    items: PlannedWorkShiftDto[];
    loading: boolean;
}

export const initialState: PlannedWorkShiftState = {
    items: [],
    loading: false,
};

export const plannedWorkShiftSlice = createSlice({
    name: "plannedWorkShift",
    initialState: initialState,
    reducers: {
        plannedWorkShiftsReceived: (
            state,
            action: PayloadAction<{
                shifts: PlannedWorkShiftDto[];
                startDate: string;
                endDate: string;
            }>
        ) => {
            // Preserve existing items outside of given range
            state.items = state.items.filter(
                (h) =>
                    h.effectiveDate < action.payload.startDate ||
                    h.effectiveDate > action.payload.endDate
            );
            state.items = [...state.items, ...action.payload.shifts];
        },
        setPlannedWorkShiftsLoading: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.loading = action.payload;
        },
    },
});

export const fetchPlannedWorkShifts = createAsyncThunk(
    "plannedWorkShift/list",
    async(params: PlannedWorkShiftQueryParameters, thunkApi) => {
        try {
            thunkApi.dispatch(setPlannedWorkShiftsLoading(true));
            const shifts = await getPlannedWorkShifts(params, thunkApi.signal);
            thunkApi.dispatch(
                plannedWorkShiftsReceived({
                    shifts,
                    startDate: params.startDate,
                    endDate: params.endDate,
                })
            );
        } finally {
            thunkApi.dispatch(setPlannedWorkShiftsLoading(false));
        }
    }
);

export const { plannedWorkShiftsReceived, setPlannedWorkShiftsLoading } =
    plannedWorkShiftSlice.actions;

export const plannedWorkShiftReducer = plannedWorkShiftSlice.reducer;

export const makeSelectPlannedWorkShiftsByDate = () =>
    createSelector(
        (state: PlannedWorkShiftState) => state.items,
        (_, rangeStart: string) => rangeStart,
        (_, _rangeStart, rangeEnd: string) => rangeEnd,
        (
            items: PlannedWorkShiftDto[],
            rangeStart: string,
            rangeEnd: string
        ) => {
            if (rangeStart && rangeEnd) {
                return items.filter((i) =>
                    Base.isBetween(
                        i.effectiveDate,
                        rangeStart,
                        rangeEnd,
                        "day",
                        "[]"
                    )
                );
            }
        }
    );

export const makeSelectPlannedWorkShiftsDurationByDate = () =>
    createSelector([makeSelectPlannedWorkShiftsByDate()], (items) =>
        items ? items.reduce((acc, cur) => acc + cur.duration, 0) : undefined
    );
